import React, { useState, useEffect, useCallback } from 'react';
import { LogOut } from 'lucide-react';
import { styles } from '../theme';
import { API_URL, fetchWithAuth, filterToUsersOwnBranches } from '../api';
import { usePosOperator } from '../hooks/usePosOperator';
import PosPinPad from './PosPinPad';
import { formatCurrency } from '../utils/formatUtils';
import { formatPHDatetime } from '../utils/dateUtils';

const formatPeso = (v) => `₱${formatCurrency(v)}`;
const formatDateTime = (v) => v ? formatPHDatetime(v) : '-';

const PosShifts = ({ token, setSidebarOpen }) => {
  const { operator, operatorToken, setOperator, clearOperator } = usePosOperator();

  useEffect(() => {
    if (!setSidebarOpen) return;
    setSidebarOpen(false);
    return () => setSidebarOpen(true);
  }, [setSidebarOpen]);

  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [currentShift, setCurrentShift] = useState(null);
  const [loadingShift, setLoadingShift] = useState(false);
  const [startingCash, setStartingCash] = useState('');
  const [endingCash, setEndingCash] = useState('');
  const [closeResult, setCloseResult] = useState(null);
  const [recentShifts, setRecentShifts] = useState([]);

  useEffect(() => {
    fetchWithAuth(`${API_URL}/branchesdropdown`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => {
        const owned = filterToUsersOwnBranches(Array.isArray(data) ? data : []);
        setBranches(owned);
        if (owned.length > 0) setBranchId(String(owned[0].id));
      })
      .catch(err => console.error('Error fetching branches:', err));
  }, [token]);

  const fetchCurrentShift = useCallback(async () => {
    if (!branchId) return;
    setLoadingShift(true);
    setCloseResult(null);
    try {
      const res = await fetchWithAuth(`${API_URL}/pos/shifts/current?branch_id=${branchId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 404) {
        setCurrentShift(null);
      } else {
        setCurrentShift(await res.json());
      }
    } catch (err) {
      console.error('Error fetching current shift:', err);
    } finally {
      setLoadingShift(false);
    }
  }, [token, branchId]);

  const fetchRecentShifts = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: 1, per_page: 5, ...(branchId ? { branch_id: branchId } : {}) });
      const res = await fetchWithAuth(`${API_URL}/pos/shifts?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setRecentShifts(data.data || []);
    } catch (err) {
      console.error('Error fetching recent shifts:', err);
    }
  }, [token, branchId]);

  useEffect(() => { fetchCurrentShift(); fetchRecentShifts(); }, [fetchCurrentShift, fetchRecentShifts]);

  const handleOpenShift = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/pos/shifts/open`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'X-POS-Operator-Token': operatorToken },
        body: JSON.stringify({ branch_id: branchId, starting_cash: startingCash || 0 })
      });
      if (res.ok) {
        setStartingCash('');
        fetchCurrentShift();
        fetchRecentShifts();
      } else {
        const err = await res.json();
        alert('Error: ' + (err.error || JSON.stringify(err.error)));
      }
    } catch (err) {
      console.error('Error opening shift:', err);
    }
  };

  const handleCloseShift = async () => {
    if (endingCash === '') {
      alert('Enter the counted cash amount');
      return;
    }
    try {
      const res = await fetchWithAuth(`${API_URL}/pos/shifts/${currentShift.id}/close`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'X-POS-Operator-Token': operatorToken },
        body: JSON.stringify({ ending_cash_counted: endingCash })
      });
      const data = await res.json();
      if (res.ok) {
        setCloseResult(data);
        setEndingCash('');
        fetchCurrentShift();
        fetchRecentShifts();
      } else {
        alert('Error: ' + (data.error || JSON.stringify(data.error)));
      }
    } catch (err) {
      console.error('Error closing shift:', err);
    }
  };

  if (!operator) {
    return <PosPinPad token={token} branchId={branchId} onVerified={(op, opToken) => setOperator(op, opToken)} />;
  }

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.title}>POS Shifts</h1>
        <button onClick={clearOperator} style={{ ...styles.button, ...styles.buttonSecondary }}>
          <LogOut size={16} /> {operator.fullname || operator.username} (Switch)
        </button>
      </div>

      <div style={{ ...styles.card, marginBottom: '20px' }}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Branch</label>
          <select value={branchId} onChange={(e) => setBranchId(e.target.value)} style={styles.input}>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
      </div>

      {loadingShift ? (
        <div style={{ ...styles.card, textAlign: 'center', color: '#6b7280' }}>Loading...</div>
      ) : currentShift ? (
        <div style={{ ...styles.card, borderLeft: '6px solid #059669' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            <div>
              <div style={{ fontSize: '13px', color: '#059669', fontWeight: 600, marginBottom: '4px' }}>SHIFT OPEN</div>
              <div style={{ fontSize: '15px', fontWeight: 600 }}>
                Opened by {currentShift.opened_by_operator_name} — {formatDateTime(currentShift.opened_at)}
              </div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>Starting cash: {formatPeso(currentShift.starting_cash)}</div>
            </div>
          </div>

          <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
            <label style={styles.label}>Close Shift — Counted Cash</label>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={endingCash}
                onChange={(e) => setEndingCash(e.target.value)}
                style={{ ...styles.input, maxWidth: '200px' }}
              />
              <button onClick={handleCloseShift} style={{ ...styles.button, ...styles.buttonDanger }}>
                Close Shift
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div style={styles.card}>
          <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '12px' }}>No shift is open for this branch</div>
          <label style={styles.label}>Starting Cash Float</label>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <input
              type="number"
              step="0.01"
              placeholder="0.00"
              value={startingCash}
              onChange={(e) => setStartingCash(e.target.value)}
              style={{ ...styles.input, maxWidth: '200px' }}
            />
            <button onClick={handleOpenShift} style={styles.button}>
              Open Shift
            </button>
          </div>
        </div>
      )}

      {closeResult && (
        <div style={{ ...styles.card, marginTop: '16px', backgroundColor: '#f9fafb' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>Shift closed</div>
          <div style={{ fontSize: '13px', color: '#374151' }}>Expected cash: {formatPeso(closeResult.expected_cash)}</div>
          <div style={{ fontSize: '13px', color: '#374151' }}>Counted cash: {formatPeso(closeResult.ending_cash_counted)}</div>
          <div style={{ fontSize: '13px', fontWeight: 600, color: closeResult.cash_variance === 0 ? '#059669' : (closeResult.cash_variance < 0 ? '#b91c1c' : '#d97706') }}>
            Variance: {closeResult.cash_variance > 0 ? '+' : ''}{formatPeso(closeResult.cash_variance)}
          </div>
        </div>
      )}

      <div style={{ marginTop: '24px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px' }}>Recent Shifts</h3>
        <div style={{ ...styles.card, padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Opened</th>
                  <th style={styles.th}>Operator</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Starting</th>
                  <th style={styles.th}>Variance</th>
                </tr>
              </thead>
              <tbody>
                {recentShifts.map(s => (
                  <tr key={s.id}>
                    <td style={styles.td}>{formatDateTime(s.opened_at)}</td>
                    <td style={styles.td}>{s.opened_by_operator_name}</td>
                    <td style={styles.td}>{s.status}</td>
                    <td style={styles.td}>{formatPeso(s.starting_cash)}</td>
                    <td style={styles.td}>{s.cash_variance === null ? '-' : formatPeso(s.cash_variance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PosShifts;
